import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import shopService, { Category } from '../../services/shop.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  CheckCircle,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ShoppingBag,
  Search as SearchIcon,
  // ícones disponíveis no picker
  ShoppingCart, Store, LocalOffer, Inventory, Storefront,
  Book, MenuBook, School, AutoStories,
  PhoneAndroid, Laptop, Headphones, Tv, SportsEsports,
  Checkroom, Diamond, Watch, ShoppingBag as BagIcon,
  Restaurant, LocalCafe, LocalPizza, Fastfood, LunchDining,
  FitnessCenter, SportsBasketball, SportsSoccer, Pool,
  Home, Weekend, Bed, Chair, Kitchen,
  LocalFlorist, Spa, BeachAccess, Park,
  Face, ContentCut, ColorLens, BrushOutlined,
  LocalHospital, Medication, HealthAndSafety,
  DirectionsCar, FlightTakeoff, Train, LocalShipping,
  Photo, Videocam, MusicNote, Movie,
  Pets, ChildCare, Toys,
  AccountBalance, AttachMoney, CreditCard,
  EmojiEvents, Star, Celebration, CardGiftcard,
  Download, CloudDownload, Article, PictureAsPdf,
} from '@mui/icons-material';

// Mapa nome → componente de icon
const ICON_MAP: Record<string, React.ElementType> = {
  ShoppingCart, Store, LocalOffer, Inventory, Storefront,
  Book, MenuBook, School, AutoStories,
  PhoneAndroid, Laptop, Headphones, Tv, SportsEsports,
  Checkroom, Diamond, Watch, ShoppingBag: BagIcon,
  Restaurant, LocalCafe, LocalPizza, Fastfood, LunchDining,
  FitnessCenter, SportsBasketball, SportsSoccer, Pool,
  Home, Weekend, Bed, Chair, Kitchen,
  LocalFlorist, Spa, BeachAccess, Park,
  Face, ContentCut, ColorLens,
  LocalHospital, Medication, HealthAndSafety,
  DirectionsCar, FlightTakeoff, Train, LocalShipping,
  Photo, Videocam, MusicNote, Movie,
  Pets, ChildCare, Toys,
  AccountBalance, AttachMoney, CreditCard,
  EmojiEvents, Star, Celebration, CardGiftcard,
  Download, CloudDownload, Article, PictureAsPdf,
  Category: CategoryIcon,
};

const ICON_LABELS: Record<string, string> = {
  ShoppingCart: 'Carrinho', Store: 'Loja', LocalOffer: 'Oferta', Inventory: 'Inventário', Storefront: 'Montra',
  Book: 'Livro', MenuBook: 'Livro Menu', School: 'Escola', AutoStories: 'Histórias',
  PhoneAndroid: 'Telemóvel', Laptop: 'Laptop', Headphones: 'Auscultadores', Tv: 'TV', SportsEsports: 'Gaming',
  Checkroom: 'Roupa', Diamond: 'Diamante', Watch: 'Relógio', ShoppingBag: 'Saco',
  Restaurant: 'Restaurante', LocalCafe: 'Café', LocalPizza: 'Pizza', Fastfood: 'Fast Food', LunchDining: 'Almoço',
  FitnessCenter: 'Ginásio', SportsBasketball: 'Basquete', SportsSoccer: 'Futebol', Pool: 'Piscina',
  Home: 'Casa', Weekend: 'Sofá', Bed: 'Cama', Chair: 'Cadeira', Kitchen: 'Cozinha',
  LocalFlorist: 'Flores', Spa: 'Spa', BeachAccess: 'Praia', Park: 'Parque',
  Face: 'Beleza', ContentCut: 'Corte', ColorLens: 'Cor',
  LocalHospital: 'Hospital', Medication: 'Medicação', HealthAndSafety: 'Saúde',
  DirectionsCar: 'Carro', FlightTakeoff: 'Avião', Train: 'Comboio', LocalShipping: 'Entrega',
  Photo: 'Foto', Videocam: 'Vídeo', MusicNote: 'Música', Movie: 'Cinema',
  Pets: 'Animais', ChildCare: 'Crianças', Toys: 'Brinquedos',
  AccountBalance: 'Banco', AttachMoney: 'Dinheiro', CreditCard: 'Cartão',
  EmojiEvents: 'Troféu', Star: 'Estrela', Celebration: 'Celebração', CardGiftcard: 'Voucher',
  Download: 'Download', CloudDownload: 'Download Cloud', Article: 'Artigo', PictureAsPdf: 'PDF',
  Category: 'Categoria',
};

interface CategoryFormData {
  name: string;
  description: string;
  icon: string;
  status: 'active' | 'inactive';
}

const Categories: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    icon: '',
    status: 'active',
  });

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label={t('shop.active')} icon={<CheckCircle />} />
    ) : (
      <Chip size="small" color="default" label={t('shop.inactive')} icon={<InactiveIcon />} />
    );
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shopService.getCategoriesWithDetails();

      if (response.success) {
        setCategories(response.data || []);
        if (response.needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError(t('shop.errorLoadingCategories'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenEdit = (category?: Category) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name,
        description: (category as any).description || '',
        icon: (category as any).icon || '',
        status: (category as any).status || 'active',
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedCategory) {
        await shopService.updateCategory(selectedCategory.id, formData);
        setSuccessMessage(t('shop.categoryUpdated'));
      } else {
        await shopService.createCategory(formData);
        setSuccessMessage(t('shop.categoryCreated'));
      }
      setEditDialog(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (err: any) {
      console.error('Error saving category:', err);
      setError(t('shop.errorSavingCategory'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (category.productCount > 0) {
      setError(t('shop.cannotDeleteCategory', { count: category.productCount }));
      return;
    }
    if (window.confirm(`${t('common.confirmDelete')} "${category.name}"?`)) {
      try {
        await shopService.deleteCategory(category.id);
        setSuccessMessage(t('shop.categoryDeleted'));
        fetchCategories();
      } catch (err: any) {
        console.error('Error deleting category:', err);
        setError(err.response?.data?.error || t('shop.errorDeletingCategory'));
      }
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await shopService.setupTables();
      if (response.success) {
        setSuccessMessage(response.message);
        setNeedsSetup(false);
        fetchCategories();
      }
    } catch (err: any) {
      console.error('Error setting up tables:', err);
      setError(t('shop.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  const totalProducts = categories.reduce((sum, c) => sum + c.productCount, 0);

  const filteredIcons = useMemo(() => {
    const q = iconSearch.toLowerCase();
    return Object.keys(ICON_MAP).filter(name =>
      !q || name.toLowerCase().includes(q) || (ICON_LABELS[name] || '').toLowerCase().includes(q)
    );
  }, [iconSearch]);

  const SelectedIconComp = formData.icon ? ICON_MAP[formData.icon] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('shop.categories')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCategories}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('shop.newCategory')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('shop.settingUp') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('shop.setupNeeded')}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {categories.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.categoriesStat')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBag color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {totalProducts}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.totalProductsStat')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.categoryHeader')}</TableCell>
                  <TableCell>{t('shop.descriptionHeader')}</TableCell>
                  <TableCell align="center">{t('shop.productsHeader')}</TableCell>
                  <TableCell>{t('shop.statusHeader')}</TableCell>
                  <TableCell align="center">{t('shop.actionsHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">{t('shop.noCategories')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {(() => {
                            const iconName = (category as any).icon;
                            const Ic = iconName && ICON_MAP[iconName] ? ICON_MAP[iconName] : CategoryIcon;
                            return <Ic color="primary" fontSize="small" />;
                          })()}
                          <Typography fontWeight={600}>{category.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(category as any).description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={category.productCount} color="primary" />
                      </TableCell>
                      <TableCell>{getStatusChip((category as any).status || 'active')}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenEdit(category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(category)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon />
          {selectedCategory ? t('shop.editCategory') : t('shop.newCategory')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.nameLabel')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('shop.descriptionLabel')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Ícone {SelectedIconComp && (
                  <Box component="span" sx={{ ml: 1, verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                    <SelectedIconComp fontSize="small" /> <Typography variant="caption">{formData.icon}</Typography>
                  </Box>
                )}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar ícone..."
                value={iconSearch}
                onChange={(e) => setIconSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 1 }}
              />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
                gap: 0.5,
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}>
                {filteredIcons.map(name => {
                  const IconComp = ICON_MAP[name];
                  const selected = formData.icon === name;
                  return (
                    <Tooltip key={name} title={ICON_LABELS[name] || name} arrow>
                      <Box
                        onClick={() => setFormData({ ...formData, icon: name })}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 0.75,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'transparent',
                          bgcolor: selected ? 'primary.50' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <IconComp fontSize="small" color={selected ? 'primary' : 'action'} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">{t('shop.active')}</MenuItem>
                <MenuItem value="inactive">{t('shop.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedCategory ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
